import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Download, Filter as FilterIcon, Loader2, Sparkles, Home, Users, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ChipBar, { useSegmentChip, segmentCounts, filterBySegment } from "@/components/SegmentChipBar";
import { toast } from "sonner";

const estadoLabels = {
  nuevo: "Nuevo", revisando: "Revisando", descartado: "Descartado",
  contactado: "Contactado", en_proceso: "En proceso", cerrado: "Cerrado",
};
const estadoCls = {
  nuevo: "bg-blue-50 text-blue-800 border-blue-200",
  revisando: "bg-amber-50 text-amber-800 border-amber-200",
  descartado: "bg-slate-100 text-slate-500 border-slate-200",
  contactado: "bg-violet-50 text-violet-800 border-violet-200",
  en_proceso: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
  cerrado: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const KpiMini = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-white border border-slate-200 p-4 flex items-center gap-4">
    <div className={`w-9 h-9 flex items-center justify-center ${accent}`}>
      <Icon className="w-4 h-4 text-white" />
    </div>
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</p>
      <p className="font-heading text-2xl font-bold text-slate-900 leading-tight">{value}</p>
    </div>
  </div>
);

export default function RadarTablero() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [chip, setChip] = useSegmentChip("oport");
  const [items, setItems] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  // Herencia de zona desde el Mapa: /radar/tablero?zona=X pre-carga el filtro.
  // Si el usuario cambia el filtro después, seguimos su elección — el param
  // es solo el "estado inicial", no un lock.
  const [filters, setFilters] = useState(() => ({
    tipo: "all", operacion: "all", fuente: "all", estado: "all",
    zona_id: searchParams.get("zona") || "all",
  }));

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filters.tipo !== "all") params.tipo = filters.tipo;
      if (filters.operacion !== "all") params.operacion = filters.operacion;
      if (filters.fuente !== "all") params.fuente = filters.fuente;
      if (filters.estado !== "all") params.estado = filters.estado;
      if (filters.zona_id !== "all") params.zona_id = filters.zona_id;
      // Note: the "senal" chip filter is applied client-side so counts stay in sync
      const [o, z] = await Promise.all([
        api.get("/opportunities", { params }),
        api.get("/zonas"),
      ]);
      setItems(o.data.items);
      setZonas(z.data.items);
    } catch (e) {
      toast.error("No se pudo cargar el tablero");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  // Chip counts (over the fine-filtered set, so users see 'Oportunidades (12)' consistent with rows)
  const counts = useMemo(() => segmentCounts(items), [items]);
  const visibleItems = useMemo(() => filterBySegment(items, chip.id), [items, chip]);

  const stats = useMemo(() => ({
    total: items.length,
    subv: items.filter(x => x.es_subvaluado).length,
    dueno: items.filter(x => x.es_dueno_directo).length,
  }), [items]);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "all" || !v) return;
      params.set(k, v);
    });
    // Chip filter → server-side flags
    if (chip.id === "oport") params.set("es_subvaluado", "true");
    if (chip.id === "dueno") params.set("es_dueno_directo", "true");
    if (chip.id === "top") { params.set("es_subvaluado", "true"); params.set("es_dueno_directo", "true"); }
    const url = `/api/opportunities/export.csv?${params.toString()}`;
    window.open(url, "_blank");
  };

  // Top 3 hero items across the currently visible set — flag them for a subtle ★ badge
  const heroIds = useMemo(() => new Set(
    visibleItems
      .filter(x => x.es_subvaluado && (x.score || 0) > 0)
      .slice()
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, 3)
      .map(x => x.opportunity_id)
  ), [visibleItems]);

  return (
    <div className="p-8 lg:p-12">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">Tablero</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Oportunidades priorizadas
          </h1>
          <p className="font-body text-base text-slate-600 mt-2">
            Ordenadas por score. Cuanto más alto, más señales de oportunidad.
          </p>
        </div>
        <Button data-testid="export-csv-btn" onClick={exportCsv} variant="outline" className="rounded-none border-slate-300">
          <Download className="w-4 h-4 mr-2" /> Exportar CSV
        </Button>
      </header>

      {/* KPIs — "Dueño directo" escondido hasta que haya señal real desde
          el actor (backlog: Motor de Demanda). Volverá con supply propio. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <KpiMini label="Oportunidades" value={stats.total} icon={Home} accent="bg-slate-900" />
        <KpiMini label="Subvaluadas" value={stats.subv} icon={Sparkles} accent="bg-emerald-700" />
      </div>

      {/* Segment chips — shared with map */}
      <div className="mb-6">
        <ChipBar current={chip.id} counts={counts} onChange={setChip} />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 p-4 mb-6 flex flex-wrap gap-3">
        <div className="flex items-center gap-2 pr-3 border-r border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
          <FilterIcon className="w-3.5 h-3.5" /> Filtros
        </div>
        <Select value={filters.tipo} onValueChange={(v) => setFilters({ ...filters, tipo: v })}>
          <SelectTrigger data-testid="filter-tipo" className="w-[140px] rounded-none border-slate-300"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {["terreno","casa","departamento","local","oficina","campo","otro"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.operacion} onValueChange={(v) => setFilters({ ...filters, operacion: v })}>
          <SelectTrigger data-testid="filter-op" className="w-[130px] rounded-none border-slate-300"><SelectValue placeholder="Operación" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="venta">Venta</SelectItem>
            <SelectItem value="alquiler">Alquiler</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.estado} onValueChange={(v) => setFilters({ ...filters, estado: v })}>
          <SelectTrigger data-testid="filter-estado" className="w-[150px] rounded-none border-slate-300"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(estadoLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.zona_id} onValueChange={(v) => setFilters({ ...filters, zona_id: v })}>
          <SelectTrigger data-testid="filter-zona" className="w-[180px] rounded-none border-slate-300"><SelectValue placeholder="Zona" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las zonas</SelectItem>
            {zonas.map(z => <SelectItem key={z.zona_id} value={z.zona_id}>{z.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.fuente} onValueChange={(v) => setFilters({ ...filters, fuente: v })}>
          <SelectTrigger data-testid="filter-fuente" className="w-[150px] rounded-none border-slate-300"><SelectValue placeholder="Fuente" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="properati">Properati</SelectItem>
            <SelectItem value="zonaprop">Zonaprop</SelectItem>
            <SelectItem value="argenprop">Argenprop</SelectItem>
            <SelectItem value="mercadolibre">MercadoLibre</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200">
        {loading ? (
          <div className="p-10 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando...</div>
        ) : visibleItems.length === 0 ? (
          <div className="p-16 text-center text-sm text-slate-500">
            No hay <strong>{chip.label.toLowerCase()}</strong> con los filtros actuales.<br />
            <span className="text-xs opacity-70">Probá con "Ver todo" o ajustá los filtros.</span>
          </div>
        ) : (
          <ul className="divide-y divide-slate-200" data-testid="opps-list">
            {visibleItems.map(o => {
              const est = estadoCls[o.estado] || estadoCls.nuevo;
              const isTopSignal = o.es_subvaluado && o.es_dueno_directo;
              const isHero = heroIds.has(o.opportunity_id);
              const cover = o.fotos?.[0];
              const disc = o.ratio_vs_mediana != null && o.ratio_vs_mediana < 1
                ? Math.round((1 - o.ratio_vs_mediana) * 100) : null;
              return (
                <li key={o.opportunity_id}
                    data-testid={`opp-row-${o.opportunity_id}`}
                    data-hero={isHero ? "true" : "false"}
                    onClick={() => navigate(`/radar/${o.opportunity_id}`)}
                    className={`p-5 hover:bg-emerald-50/40 cursor-pointer transition-colors border-l-4 group ${
                      isTopSignal ? "border-emerald-600" : o.es_subvaluado ? "border-emerald-300" : "border-transparent"
                    }`}>
                  <div className="flex items-start gap-5">
                    {/* Cover photo */}
                    <div className="w-40 h-28 shrink-0 bg-slate-100 border border-slate-200 overflow-hidden relative">
                      {cover ? (
                        <img src={cover} alt="" loading="lazy"
                             className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                             onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                          <Home className="w-6 h-6" />
                        </div>
                      )}
                      {disc != null && disc >= 5 && (
                        <div className="absolute top-1.5 left-1.5 bg-emerald-700 text-white text-[10px] font-heading font-bold px-1.5 py-0.5 tracking-wide shadow-sm">
                          −{disc}%
                        </div>
                      )}
                      {isHero && (
                        <div className="absolute top-1.5 right-1.5 bg-amber-500 text-white text-[10px] font-heading font-bold px-1.5 py-0.5 tracking-wide shadow-md flex items-center gap-1">
                          <span>★</span> TOP
                        </div>
                      )}
                    </div>

                    {/* Score */}
                    <div className="w-14 text-right pt-1 shrink-0">
                      <p className={`font-heading text-3xl font-bold leading-none ${o.score >= 30 ? "text-emerald-700" : o.score > 5 ? "text-emerald-600" : "text-slate-400"}`}>{o.score || 0}</p>
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">score</p>
                    </div>

                    {/* Body */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{o.tipo} · {o.operacion}</span>
                        <span className="text-slate-300">·</span>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{o.fuente}</span>
                        {o.es_subvaluado && <span className="text-[10px] uppercase font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5">Subvaluado</span>}
                        {o.es_dueno_directo && <span className="text-[10px] uppercase font-bold text-emerald-900 bg-emerald-200 border border-emerald-300 px-1.5 py-0.5">Dueño directo</span>}
                        {o.ubicacion_aproximada && (
                          <span className="text-[10px] uppercase font-bold text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5" title="Este aviso no traía coords precisas — lo incluimos por match textual con la zona">
                            Ubicación aprox
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading text-base font-semibold text-slate-900 line-clamp-2 mb-1.5 group-hover:text-emerald-800 transition-colors">{o.titulo}</h3>
                      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
                        <span className="font-heading text-xl font-bold text-slate-900">{o.moneda} {Number(o.precio).toLocaleString("es-AR")}</span>
                        <span className="text-slate-500">{o.superficie_m2} m²</span>
                        <span className="text-slate-500">USD/m² <strong className="text-slate-900">{Math.round(o.precio_m2 || 0)}</strong>
                          {o.precio_m2_mediana_zona && <span className="opacity-70"> · mediana {Math.round(o.precio_m2_mediana_zona)}</span>}
                        </span>
                        {disc != null && disc >= 5 && (
                          <span className="text-emerald-700 font-heading font-bold text-sm flex items-center gap-1">
                            <TrendingUp className="w-3.5 h-3.5" /> −{disc}% vs mediana
                          </span>
                        )}
                      </div>
                      {o.motivos?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {o.motivos.slice(0, 3).map((m) => (
                            <span key={m} className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                              ✓ {m}
                            </span>
                          ))}
                          {o.motivos.length > 3 && (
                            <span className="text-[11px] text-slate-500 px-1">+{o.motivos.length - 3}</span>
                          )}
                        </div>
                      )}
                      {o.ref_label && (
                        <p className="text-[10px] text-slate-500 mt-2 italic" data-testid={`ref-label-${o.opportunity_id}`}>
                          {o.ref_label}
                        </p>
                      )}
                    </div>

                    {/* Estado */}
                    <div className="text-right shrink-0">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border ${est}`}>
                        {estadoLabels[o.estado] || o.estado}
                      </span>
                      <p className="text-[11px] text-slate-500 mt-2 truncate max-w-[140px]">{o.localidad}</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
